import { getWatermarkMarkup } from './utils/SettingsManager.utils.watermark'
import { applyFontOverride } from './utils/SettingsManager.utils.fonts'
import { AALogger } from './utils/SettingsManager.utils.logger'
import { LoggingLevel } from '@/components/webcomponents/Global/logging'
import { GLOBAL_VERSION_CONTEXT } from '@/components/webcomponents/Global/versioncontext'
import { joinUrl, resolveUrl } from '@/config/urls.js'
import { registerBootstrapIconLibrary } from '@/components/webcomponents/Global/loadDependency'
import type { } from '@/components/webcomponents/Global/global'

const USAGE_LOGGING_PATH = 'usage.json'

class SettingsManager extends HTMLElement {
  private aaLogger: AALogger | null = null
  // Opt-in via enable-usage="true": the usage.json CDN endpoint doesn't exist yet, so this stays off by default to avoid guaranteed 404s.
  private _logUsage: boolean = false

  constructor() {
    super()

    document.AscendAwesome = document.AscendAwesome || {}
    if (document.AscendAwesome.version === undefined) {
      document.AscendAwesome.version = GLOBAL_VERSION_CONTEXT.VERSION ?? 'unknown'
      document.AscendAwesome.theme = 'default'
      document.AscendAwesome.components = new Map<string, HTMLElement>()
    }

    this.aaLogger = new AALogger()

    // Register Bootstrap icon library so wa-icon elements with library="bootstrap" work.
    registerBootstrapIconLibrary()

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.innerHTML = '<!-- Settings Manager -->'
  }

  static get observedAttributes() {
    return ['font', 'disable-watermark', 'log-level', 'allow-audit', 'enable-usage']
  }

  connectedCallback() {
    this.updateWatermark()
    this.logUsage()
  }

  private logUsage() {
    if (!this._logUsage) {
      return
    }
    try {
      const cdnRoot = GLOBAL_VERSION_CONTEXT.CDN_ROOT
      if (!cdnRoot) {
        document?.AscendAwesome?.LogManager?.logDebug(
          'CDN_ROOT is not defined, skipping usage logging',
        )
        return
      }
      const version = this.sanitize(document?.AscendAwesome?.version) || 'unknown'
      const domain = this.sanitize(window.location.hostname)
      const font = this.sanitize(this.getAttribute('font')) || 'default'
      const usageUrl = resolveUrl(joinUrl(cdnRoot, USAGE_LOGGING_PATH), window.location.href, {
        v: version,
        site: domain,
        font,
      })

      document?.AscendAwesome?.LogManager?.logDebug('logging usage')

      // Fire and forget API call for usage tracking
      fetch(usageUrl).catch(() => {
        // Silently ignore errors for fire-and-forget logging
      })
    } catch {
      document?.AscendAwesome?.LogManager?.logDebug('usage failed')
    }
  }

  private sanitize(value: string | null | undefined): string {
    return value ? value.replace(/[^a-zA-Z0-9\-\.]/g, '') : ''
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'font') {
      applyFontOverride(newValue)
    } else if (name === 'disable-watermark') {
      this.updateWatermark()
    } else if (name === 'log-level') {
      // Handle log level change
      const logLevel = this.stringToSettingsManagerLogLevel(newValue)
      this.aaLogger?.setLogLevel(logLevel || LoggingLevel.Default)
    } else if (name === 'allow-audit') {
      this.updateWatermark()
    } else if (name === 'enable-usage') {
      this._logUsage = newValue !== 'false'
      this.logUsage() // always call
    }
  }

  private updateWatermark() {
    const disableWatermark = this.getAttribute('disable-watermark') === 'true'
    const allowAudit = this.getAttribute('allow-audit') === 'true'
    this.applyWatermark(disableWatermark, allowAudit)
  }

  applyWatermark(disableWatermark: boolean, allowAudit: boolean) {
    if (this.shadowRoot) {
      this.shadowRoot.innerHTML =
        getWatermarkMarkup(disableWatermark, allowAudit) + '<!-- Settings Manager -->'
      this.shadowRoot.getElementById('btn-show-audit-ui')?.addEventListener('click', () => {
        document.AscendAwesome?.LogManager?.logDebug('Audit UI button clicked')
        if (document.getElementsByTagName('ts-auditmanager')?.length === 0) {
          const auditManager = document.createElement('ts-auditmanager') as HTMLElement
          auditManager?.setAttribute('show-ui', 'true')
          const settingManager = document.getElementsByTagName('ts-settings-manager')[0]
          settingManager?.parentElement?.insertBefore(auditManager, settingManager.nextSibling)
          document.AscendAwesome?.LogManager?.logDebug('Audit UI added to the page')
        } else {
          document.AscendAwesome?.LogManager?.logDebug('Audit UI already exists')
        }
      })
    }
  }

  disconnectedCallback() {
    applyFontOverride(null)
  }

  // type safe conversion from string to SettingsManagerFont enum
  private stringToSettingsManagerLogLevel(value: string): LoggingLevel | null {
    if (Object.values(LoggingLevel).includes(value as LoggingLevel)) {
      return value as LoggingLevel
    }
    return null
  }
}

if (!customElements.get('ts-settings-manager')) {
  customElements.define('ts-settings-manager', SettingsManager)
}

export { SettingsManager }
